
import { getApps, initializeApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  signInWithCredential,
  EmailAuthProvider,
  linkWithCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut
} from 'firebase/auth';
import { getAnalytics, isSupported, logEvent, setUserProperties } from 'firebase/analytics';
import { getFirestore, doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import * as Sentry from '@sentry/react';
import { ENV } from '../config/env.js';
import { trackPixels } from '../analytics/pixels.js';

let firebaseApp = null;
let analyticsInstance = null;
let analyticsInitDone = false;
// trackEvent() can be called (e.g. from a component's mount effect) before Firebase
// Analytics finishes its own async init — without this queue those early calls would
// just silently vanish instead of being dropped-or-sent once analyticsInstance is ready.
let pendingAnalyticsEvents = [];
let authInstance = null;
let currentUser = null;
let dbInstance = null;
let storageInstance = null;
let disableAutoAnon = false; // runtime flag to prevent auto-guest during purchase/login funnels
let sessionLastAuthAt = null;

function getFirebaseConfig() {
    return {
        apiKey: ENV.firebase.apiKey,
        authDomain: ENV.firebase.authDomain,
        projectId: ENV.firebase.projectId,
        storageBucket: ENV.firebase.storageBucket,
        messagingSenderId: ENV.firebase.messagingSenderId,
        appId: ENV.firebase.appId,
        measurementId: ENV.firebase.measurementId,
    };
}

export async function initFirebase() {
    if (firebaseApp) return;
    const config = getFirebaseConfig();
    // Only init if required keys exist (allow missing appId in local/dev)
    if (!config.apiKey) {
        console.warn('Firebase config missing. Analytics disabled.');
        return;
    }

    if (getApps().length === 0) {
        firebaseApp = initializeApp(config);
    } else {
        firebaseApp = getApps()[0];
    }

    // Auth init
    try {
        authInstance = getAuth(firebaseApp);
        // Explicit local (IndexedDB) persistence so anonymous/guest sessions survive
        // a hard reload or deep link instead of silently starting a new identity.
        try { await setPersistence(authInstance, browserLocalPersistence); } catch (_) {}
        onAuthStateChanged(authInstance, (user) => {
            currentUser = user;
            try {
                sessionLastAuthAt = Date.now();
                if (user) {
                    localStorage.setItem('ff:lastAuthUid', user.uid);
                }
            } catch (_) {}

            // Best-effort sync of basic user profile into Firestore
            try {
                if (user && dbInstance) {
                    const providerId = (user.providerData && user.providerData[0] && user.providerData[0].providerId) || (user.isAnonymous ? 'anonymous' : 'password');
                    // Do not await to avoid blocking auth flow
                    (async () => {
                        try {
                            const userRef = doc(dbInstance, 'users', user.uid);
                            if (user.isAnonymous) {
                                // Ensure an anonymous user doc exists with a default creditsBalance
                                const snap = await getDoc(userRef);
                                if (!snap.exists()) {
                                    await setDoc(userRef, { isAnonymous: true, creditsBalance: 30, createdAt: serverTimestamp(), lastLoginAt: serverTimestamp(), ...(getSessionKey() ? { sessionKey: getSessionKey() } : {}) }, { merge: true });
                                } else {
                                    await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
                                }
                            } else {
                                await setDoc(
                                    userRef,
                                    {
                                        email: user.email || null,
                                        displayName: user.displayName || null,
                                        provider: providerId, lastLoginAt: serverTimestamp(), ...(getSessionKey() ? { lastSessionKey: getSessionKey() } : {}),
                                    },
                                    { merge: true }
                                );
                            }
                        } catch (_) {}
                    })();
                }
            } catch (_) {}
        });
        // Wait briefly for any persisted user (Google/email) to restore before falling back to guest
        let restoredUser = null;
        try { restoredUser = await waitForUserAvailable(3500); } catch (_) {}
        // Respect runtime flag to suppress auto-anon during gated flows
        if (!disableAutoAnon && !restoredUser && !authInstance.currentUser) {
            try { await signInAnonymously(authInstance); } catch (_) {}
            try { await waitForUserAvailable(3000); } catch (_) {}
        }
    } catch (err) {
        console.warn('Auth initialization error:', err);
    }

    try {
        const supported = await isSupported();
        if (supported && config.measurementId) {
            analyticsInstance = getAnalytics(firebaseApp);
        } else {
            console.warn('Firebase Analytics not supported in this environment.');
        }
    } catch (err) {
        console.warn('Analytics initialization error:', err);
    } finally {
        analyticsInitDone = true;
        if (analyticsInstance && pendingAnalyticsEvents.length) {
            const queued = pendingAnalyticsEvents;
            pendingAnalyticsEvents = [];
            queued.forEach(({ eventName, params }) => {
                try { logEvent(analyticsInstance, eventName, params); } catch (_) { }
            });
        } else {
            pendingAnalyticsEvents = [];
        }
    }

    // Firestore init (safe to call even if not used)
    try {
        dbInstance = getFirestore(firebaseApp);
    } catch (err) {
        console.warn('Firestore initialization error:', err);
        dbInstance = null;
    }

    // Storage init (safe to call even if not used)
    try {
        storageInstance = getStorage(firebaseApp);
    } catch (err) {
        console.warn('Storage initialization error:', err);
        storageInstance = null;
    }
}

export function getStorageInstance() {
    return storageInstance;
}

// Uploads a single file to `users/{uid}/cardImages/{imageId}/{filename}` and returns its
// public download URL. Storage security rules restrict read/write to the owning user (see
// firebase/storage.rules) — images are never shared or reused across users, only ever
// displayed back to the person who attached them to their own card.
export async function uploadCardImage(uid, file) {
    if (!uid || !file) throw new Error('Missing uid or file for image upload');
    await initFirebase();
    if (!storageInstance) throw new Error('Storage not initialized');
    const imageId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const safeName = (file.name || 'image').replace(/[^a-zA-Z0-9_.-]/g, '_');
    const path = `users/${uid}/cardImages/${imageId}/${safeName}`;
    const ref = storageRef(storageInstance, path);
    await uploadBytes(ref, file, { contentType: file.type || 'image/png' });
    return await getDownloadURL(ref);
}

// Saves the source document (PDF, etc.) a user studied in the document study modal, scoped
// to the folder it was used for, so it can be reopened later without re-uploading. Mirrors
// uploadCardImage's shape/pattern exactly.
export async function uploadSourceDocument(uid, folderId, file, docId) {
    if (!uid || !folderId || !file) throw new Error('Missing uid, folderId, or file for document upload');
    await initFirebase();
    if (!storageInstance) throw new Error('Storage not initialized');
    const safeName = (file.name || 'document').replace(/[^a-zA-Z0-9_.-]/g, '_');
    const path = `users/${uid}/folders/${folderId}/sourceDocuments/${docId}/${safeName}`;
    const ref = storageRef(storageInstance, path);
    await uploadBytes(ref, file, { contentType: file.type || 'application/pdf' });
    return await getDownloadURL(ref);
}

// Any of these firing means a generation genuinely succeeded (all called post-await, after
// the backend call resolves) — the real "aha moment" for ad-platform activation tracking,
// as opposed to registration or even just opening the app.
const GENERATION_SUCCESS_EVENTS = new Set(['generation_live', 'generation_foto', 'generation_upload', 'notes_generate', 'exam_created']);
// Exported so other modules (e.g. InstallAppBanner's "has this person actually made
// something yet" gate) can check the same flag without duplicating the key string.
export const FIRST_GENERATION_FLAG = 'ff:firstGenerationFired';
export const hasGeneratedBefore = () => {
    try { return localStorage.getItem(FIRST_GENERATION_FLAG) === '1'; } catch (_) { return false; }
};

export function trackEvent(eventName, params = {}) {
    try {
        if (analyticsInstance) {
            logEvent(analyticsInstance, eventName, params);
        } else if (!analyticsInitDone) {
            // Analytics hasn't finished initializing yet — queue rather than drop, and cap
            // it so a page that never calls initFirebase() can't leak memory.
            pendingAnalyticsEvents.push({ eventName, params });
            if (pendingAnalyticsEvents.length > 25) pendingAnalyticsEvents.shift();
        }
    } catch (err) {
        // Swallow analytics errors to avoid impacting UX
    }
    // Fire the ad-platform "FirstGeneration" pixel once per browser, the first time any
    // generation actually succeeds — deliberately NOT once per event, so a power user
    // generating a dozen decks doesn't inflate activation counts. GA4 still gets every
    // individual generation_* event above for its own (unlimited) reporting.
    if (GENERATION_SUCCESS_EVENTS.has(eventName)) {
        try {
            if (!localStorage.getItem(FIRST_GENERATION_FLAG)) {
                localStorage.setItem(FIRST_GENERATION_FLAG, '1');
                trackPixels('FirstGeneration');
                // localStorage's own 'storage' event only fires in OTHER tabs, not this one —
                // dispatch a same-tab signal so UI gated on hasGeneratedBefore() (e.g. the
                // install banner) can react immediately instead of only picking this up on
                // the next full page load.
                try { window.dispatchEvent(new Event('ff:firstGeneration')); } catch (_) {}
            }
        } catch (_) {
            // Swallow — localStorage can throw in some privacy modes
        }
    }
}

export function setAnalyticsUserProps(props = {}) {
    try {
        if (analyticsInstance) setUserProperties(analyticsInstance, props);
    } catch (err) {
        // Swallow analytics errors to avoid impacting UX
    }
}

export function getFirebaseAppUnsafe() {
    return firebaseApp;
}

export function getDb() {
    return dbInstance;
}

// Wait for Firestore DB to be initialized and available
export async function waitForDbAvailable(timeoutMs = 3000) {
    try {
        if (dbInstance) return dbInstance;
        await initFirebase();
        if (dbInstance) return dbInstance;
        return await new Promise((resolve) => {
            const start = Date.now();
            const interval = setInterval(() => {
                if (dbInstance) {
                    clearInterval(interval);
                    resolve(dbInstance);
                } else if (Date.now() - start > timeoutMs) {
                    clearInterval(interval);
                    resolve(null);
                }
            }, 100);
        });
    } catch (_) {
        return null;
    }
}

export async function getIdToken() {
    try {
        if (!authInstance) return null;
        const user = authInstance.currentUser || currentUser;
        if (!user) return null;
        return await user.getIdToken();
    } catch {
        return null;
    }
}

// Wait briefly for Firebase to restore the user after a refresh
export async function waitForUserAvailable(timeoutMs = 3000) {
    try {
        if (!authInstance) return null;
        const existingUser = authInstance.currentUser || currentUser;
        if (existingUser) return existingUser;
        return await new Promise((resolve) => {
            let resolved = false;
            const timer = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    try { unsub(); } catch (_) {}
                    resolve(null);
                }
            }, timeoutMs);
            const unsub = onAuthStateChanged(authInstance, (user) => {
                if (user && !resolved) {
                    resolved = true;
                    clearTimeout(timer);
                    try { unsub(); } catch (_) {}
                    resolve(user);
                }
            });
        });
    } catch (_) {
        return null;
    }
}

export async function authFetch(url, options = {}) {
    // Helper to actually call fetch with a specific token
    const doFetch = async (tokenToUse) => {
        const headers = { ...(options.headers || {}) };
        if (tokenToUse) headers['Authorization'] = `Bearer ${tokenToUse}`;
        try {
            const sk = getSessionKey();
            if (sk) headers['X-FF-Session'] = sk;
        } catch (_) {}
        try { const lastUid = localStorage.getItem('ff:lastAuthUid'); if (lastUid) headers['X-FF-LastUid'] = lastUid; } catch (_) {}
        return fetch(url, { ...options, headers });
    };

    try { await initFirebase(); } catch (_) {}

    // Ensure we have a user/token after refresh before first attempt
    let token = await getIdToken();
    if (!token) {
        await waitForUserAvailable(3000);
        token = await getIdToken();
    }
    let res = await doFetch(token);

    // On 401/403, try to refresh token once, then retry
    if (res && (res.status === 401 || res.status === 403)) {
        try {
            const freshToken = authInstance && (authInstance.currentUser || currentUser)
                ? await (authInstance.currentUser || currentUser).getIdToken(true)
                : null;
            if (freshToken) {
                res = await doFetch(freshToken);
            }
        } catch (_) {}
        // If still unauthorized, notify handler
        if (res && (res.status === 401 || res.status === 403)) {
            if (unauthorizedHandler) {
                try { unauthorizedHandler(res); } catch (_) {}
            }
        }
    }

    return res;
}

export function getApiErrorMessage(error, fallbackMessage = '') {
    if (!error) return fallbackMessage || '';
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message || fallbackMessage || String(error);

    const normalizePiece = (value) => {
        if (value == null) return '';
        if (typeof value === 'string') return value;
        if (value instanceof Error) return value.message || '';
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (typeof value === 'object') {
            if (typeof value.message === 'string') return value.message;
            if (typeof value.error?.message === 'string') return value.error.message;
            try { return JSON.stringify(value); } catch (_) { return String(value); }
        }
        return String(value);
    };

    if (typeof error === 'object') {
        const message = normalizePiece(error.message || error.msg || error.error?.message || error.error?.msg);
        const code = normalizePiece(error.code || error.error?.code);
        if (message && code) return `${code}: ${message}`;
        if (message) return message;
        if (code) return code;
        try { return JSON.stringify(error); } catch (_) { return String(error); }
    }

    return normalizePiece(error);
}

export class ApiError extends Error {
    constructor({ code, message, requestId = null, details, status, url } = {}) {
        const safeCode = typeof code === 'string' && code ? code : 'UNKNOWN_ERROR';
        const safeMessage = typeof message === 'string' && message ? message : 'Unknown error';
        const safeStatus = typeof status === 'number' ? status : undefined;
        const safeUrl = typeof url === 'string' ? url : undefined;
        const safeRequestId = typeof requestId === 'string' && requestId ? requestId : null;
        super(safeMessage);
        this.name = 'ApiError';
        this.code = safeCode;
        this.requestId = safeRequestId;
        this.details = details;
        this.status = safeStatus;
        this.url = safeUrl;
    }
}

function reportApiErrorToSentry(err) {
    if (!ENV.analytics.sentryDsn) return;
    try {
        if (err.status >= 500 || err.status === undefined || err.code === 'INVALID_API_RESPONSE') {
            Sentry.withScope(scope => {
                if (err.url) scope.setTag("api.url", err.url);
                if (err.status) scope.setTag("api.status", err.status);
                if (err.code) scope.setTag("api.code", err.code);
                if (err.requestId) scope.setTag("api.requestId", err.requestId);
                Sentry.captureException(err);
            });
        }
    } catch (_) {}
}

const IDEMPOTENCY_FINGERPRINTS = new Map();
const IDEMPOTENCY_FINGERPRINTS_MAX = 500;

export function createIdempotencyKey() {
    try {
        if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    } catch (_) {}
    try {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch (_) {
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
}

function buildIdempotencyFingerprint(url, options = {}) {
    const method = String(options.method || 'GET').toUpperCase();
    const body = options.body;
    const bodyPart = typeof body === 'string' ? body : body == null ? '' : '[non-string-body]';
    return `${method} ${String(url)} ${bodyPart}`;
}

function bindIdempotencyKeyToFingerprint(key, fingerprint) {
    if (!key || !fingerprint) return;
    const existing = IDEMPOTENCY_FINGERPRINTS.get(key);
    if (existing && existing !== fingerprint) {
        throw new Error('Idempotency-Key reused with a different request payload');
    }
    if (!existing) {
        IDEMPOTENCY_FINGERPRINTS.set(key, fingerprint);
        if (IDEMPOTENCY_FINGERPRINTS.size > IDEMPOTENCY_FINGERPRINTS_MAX) {
            const firstKey = IDEMPOTENCY_FINGERPRINTS.keys().next().value;
            if (firstKey) IDEMPOTENCY_FINGERPRINTS.delete(firstKey);
        }
    }
}

function getRequestIdFromResponse(res) {
    return (res && res.headers && res.headers.get && (res.headers.get('x-request-id') || res.headers.get('X-Request-Id'))) || null;
}

function parseApiEnvelopeJson(json, { requestId, status, url } = {}) {
    const isObj = json && typeof json === 'object' && !Array.isArray(json);
    const hasSuccess = isObj && typeof json.success === 'boolean';
    if (!hasSuccess) {
        const fallbackReqId = requestId || json?.error?.requestId || json?.requestId || null;
        const err = new ApiError({ code: 'INVALID_API_RESPONSE', message: 'Invalid API response', requestId: fallbackReqId, status, url });
        reportApiErrorToSentry(err);
        throw err;
    }

    if (json.success === true) {
        return json.data;
    }

    const errObj = json.error;
    if (!errObj || typeof errObj !== 'object') {
        const err = new ApiError({ code: 'INVALID_API_RESPONSE', message: 'Invalid API response', requestId: requestId || null, status, url });
        reportApiErrorToSentry(err);
        throw err;
    }
    const code = typeof errObj.code === 'string' && errObj.code ? errObj.code : 'UNKNOWN_ERROR';
    const message = typeof errObj.message === 'string' && errObj.message ? errObj.message : 'Unknown error';
    const rid = requestId || (typeof errObj.requestId === 'string' ? errObj.requestId : null);
    const details = errObj.details;
    const err = new ApiError({ code, message, requestId: rid, details, status, url });
    reportApiErrorToSentry(err);
    throw err;
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function computeBackoffMs(attempt, { initialBackoffMs, maxBackoffMs }) {
    const base = Math.min(maxBackoffMs, initialBackoffMs * Math.pow(2, attempt));
    const jitter = Math.floor(Math.random() * Math.min(125, Math.max(25, Math.floor(base * 0.2))));
    return base + jitter;
}

export async function parseApiEnvelopeResponse(res, { url } = {}) {
    const requestId = getRequestIdFromResponse(res);
    let json = null;
    try {
        json = await res.json();
    } catch (_) {
        const err = new ApiError({ code: 'INVALID_API_RESPONSE', message: 'Invalid API response', requestId, status: res?.status, url });
        reportApiErrorToSentry(err);
        throw err;
    }
    return parseApiEnvelopeJson(json, { requestId, status: res?.status, url });
}

export async function authApi(url, options = {}) {
    let res;
    try {
        res = await authFetch(url, options);
    } catch (err) {
        if (err instanceof ApiError) reportApiErrorToSentry(err);
        else reportApiErrorToSentry(new ApiError({ code: 'NETWORK_ERROR', message: err.message, url }));
        throw err;
    }
    return await parseApiEnvelopeResponse(res, { url });
}

export async function authApiIdempotent(
    url,
    options = {},
    {
        idempotencyKey,
        maxRetries = 3,
        initialBackoffMs = 250,
        maxBackoffMs = 1500
    } = {}
) {
    const method = String(options.method || 'GET').toUpperCase();
    if (method !== 'POST') return await authApi(url, { ...options, method });

    const keyToUse = typeof idempotencyKey === 'string' && idempotencyKey ? idempotencyKey : createIdempotencyKey();
    const fingerprint = buildIdempotencyFingerprint(url, { ...options, method });
    bindIdempotencyKeyToFingerprint(keyToUse, fingerprint);

    const baseHeaders = { ...(options.headers || {}) };
    baseHeaders['Idempotency-Key'] = keyToUse;

    let attempt = 0;
    for (;;) {
        let res = null;
        try {
            res = await authFetch(url, { ...options, method, headers: baseHeaders });
        } catch (err) {
            if (attempt < maxRetries) {
                const delayMs = computeBackoffMs(attempt, { initialBackoffMs, maxBackoffMs });
                attempt += 1;
                await sleep(delayMs);
                continue;
            }
            if (err instanceof ApiError) reportApiErrorToSentry(err);
            else reportApiErrorToSentry(new ApiError({ code: 'NETWORK_ERROR', message: err.message, url }));
            throw err;
        }

        if (res && res.status === 409) {
            const requestId = getRequestIdFromResponse(res);
            let json = null;
            try {
                json = await res.json();
            } catch (_) {
                const err = new ApiError({ code: 'INVALID_API_RESPONSE', message: 'Invalid API response', requestId, status: res?.status, url });
                reportApiErrorToSentry(err);
                throw err;
            }

            const errCode = json?.error?.code || json?.code || null;
            if (errCode === 'IDEMPOTENCY_IN_PROGRESS' && attempt < maxRetries) {
                const delayMs = computeBackoffMs(attempt, { initialBackoffMs, maxBackoffMs });
                attempt += 1;
                await sleep(delayMs);
                continue;
            }

            return parseApiEnvelopeJson(json, { requestId, status: res?.status, url });
        }

        if (res && (res.status === 429 || res.status === 502 || res.status === 503 || res.status === 504) && attempt < maxRetries) {
            const retryAfter = res.headers && res.headers.get ? res.headers.get('Retry-After') : null;
            const retryAfterSec = retryAfter ? Number(retryAfter) : NaN;
            const headerDelayMs = Number.isFinite(retryAfterSec) ? Math.max(0, Math.floor(retryAfterSec * 1000)) : 0;
            const backoffDelayMs = computeBackoffMs(attempt, { initialBackoffMs, maxBackoffMs });
            const delayMs = Math.max(headerDelayMs, backoffDelayMs);
            attempt += 1;
            await sleep(delayMs);
            continue;
        }

        return await parseApiEnvelopeResponse(res, { url });
    }
}

// --- Auth helpers ---
export function getAuthInstance() {
  return authInstance;
}

export function onAuth(callback) {
  if (!authInstance) return () => {};
  return onAuthStateChanged(authInstance, callback);
}

let unauthorizedHandler = null;
export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = typeof handler === 'function' ? handler : null;
}

// Lightweight anonymous session key for backend fallback when ID token is missing/invalid
export function getSessionKey() {
  try {
    let key = localStorage.getItem('ff:sessionKey');
    if (!key) {
      // Generate a 32-byte random key (base64url)
      const bytes = new Uint8Array(32);
      crypto.getRandomValues(bytes);
      const b64 = btoa(String.fromCharCode.apply(null, bytes));
      key = b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
      localStorage.setItem('ff:sessionKey', key);
    }
    return key;
  } catch (_) {
    return null;
  }
}

// TikTok/Instagram/Facebook's built-in in-app browsers frequently block or silently break
// popup-based OAuth — Google's own security policy rejects sign-in from many embedded
// WebViews outright ("disallowed_useragent"), and even when it doesn't, popups opened from
// a tap inside these browsers are often blocked or lose the postMessage handshake back to
// the opener. This is a well-documented, common failure mode for exactly this kind of
// ad-driven mobile traffic. Detect it and use a full-page redirect instead, which routes
// around the popup-specific failures (see completeGoogleRedirectIfPresent below for the
// other half — picking the result back up after the redirect returns).
function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /TikTok|BytedanceWebview|Instagram|FBAN|FBAV|Line\//i.test(ua);
}

export async function signInWithGoogle() {
  if (!authInstance) throw new Error('Auth not initialized');
  const provider = new GoogleAuthProvider();

  if (isInAppBrowser()) {
    disableAutoAnon = true;
    try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
    if (authInstance.currentUser && authInstance.currentUser.isAnonymous) {
      await linkWithRedirect(authInstance.currentUser, provider);
    } else {
      await signInWithRedirect(authInstance, provider);
    }
    // The page navigates away here — nothing after this line runs in this load.
    return null;
  }

  let res = null;
  try {
    disableAutoAnon = true;
    // If we're currently signed in anonymously, prefer linking to avoid conflicts
    if (authInstance.currentUser && authInstance.currentUser.isAnonymous) {
      try {
        res = await linkWithPopup(authInstance.currentUser, provider);
      } catch (linkErr) {
        // If link fails due to existing account, reuse credential from the first popup to avoid a second popup
        const cred = GoogleAuthProvider.credentialFromError(linkErr);
        if (cred) {
          try { await signOut(authInstance); } catch (_) {}
          res = await signInWithCredential(authInstance, cred);
        } else {
          try { await signOut(authInstance); } catch (_) {}
          res = await signInWithPopup(authInstance, provider);
        }
      }
    } else {
      res = await signInWithPopup(authInstance, provider);
    }
  } catch (err) {
    // Try to recover with a credential if available to avoid opening a second popup
    const cred = GoogleAuthProvider.credentialFromError(err);
    if (cred) {
      try { await signOut(authInstance); } catch (_) {}
      res = await signInWithCredential(authInstance, cred);
    } else {
      // As last resort, ensure anonymous state is cleared and retry once
      try { await signOut(authInstance); } catch (_) {}
      res = await signInWithPopup(authInstance, provider);
    }
  }
  try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
  disableAutoAnon = false;
  return res;
}

// Completes a signInWithRedirect/linkWithRedirect started by signInWithGoogle's in-app-
// browser branch — call once on app boot (mirrors completeMagicLinkIfPresent). Returns the
// same UserCredential shape as signInWithPopup (including additionalUserInfo.isNewUser) so
// the caller can fire the same signup-vs-login tracking, or null if there was no pending
// redirect to complete.
export async function completeGoogleRedirectIfPresent() {
  if (!authInstance) return null;
  try {
    const res = await getRedirectResult(authInstance);
    return res || null;
  } catch (_) {
    return null;
  } finally {
    disableAutoAnon = false;
  }
}

export async function sendMagicLink(email) {
  if (!authInstance) throw new Error('Auth not initialized');
  const actionCodeSettings = {
    url: `${window.location.origin}/`,
    handleCodeInApp: true
  };
  window.localStorage.setItem('ff-magic-email', email);
  await sendSignInLinkToEmail(authInstance, email, actionCodeSettings);
}

export async function completeMagicLinkIfPresent() {
  if (!authInstance) return null;
  const href = window.location.href;
  if (isSignInWithEmailLink(authInstance, href)) {
    let email = window.localStorage.getItem('ff-magic-email');
    if (!email) {
      email = window.prompt('Please confirm your email for sign in');
    }
    disableAutoAnon = true;
    try {
      // Prefer linking to preserve the current anonymous session's state
      if (authInstance.currentUser && authInstance.currentUser.isAnonymous) {
        try {
          const credential = EmailAuthProvider.credentialWithLink(email, href);
          const linked = await linkWithCredential(authInstance.currentUser, credential);
          try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
          return linked;
        } catch (linkErr) {
          // Fall back to full sign-in if linking fails (e.g., credential already in use)
          try { await signOut(authInstance); } catch (_) {}
          const res = await signInWithEmailLink(authInstance, email, href);
          try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
          return res;
        }
      }
      // Non-anonymous session: complete sign-in normally
      const res = await signInWithEmailLink(authInstance, email, href);
      try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
      return res;
    } finally {
      disableAutoAnon = false;
      window.localStorage.removeItem('ff-magic-email');
    }
  }
  return null;
}

export async function signOutUser() {
  if (!authInstance) return;
  await signOut(authInstance);
}

export { signInAnonymously };
export function setDisableAutoAnon(value) {
  disableAutoAnon = Boolean(value);
}

// Email/password auth helpers (preserve anon session by linking when possible)
export async function signInWithEmailPassword(email, password) {
  if (!authInstance) throw new Error('Auth not initialized');
  disableAutoAnon = true;
  try {
    if (authInstance.currentUser && authInstance.currentUser.isAnonymous) {
      // Link credentials to upgrade anonymous account
      const cred = EmailAuthProvider.credential(email, password);
      try {
        const res = await linkWithCredential(authInstance.currentUser, cred);
        try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
        return res;
      } catch (linkErr) {
        try { await signOut(authInstance); } catch (_) {}
        const res = await signInWithEmailAndPassword(authInstance, email, password);
        try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
        return res;
      }
    }
    const res = await signInWithEmailAndPassword(authInstance, email, password);
    try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
    return res;
  } finally {
    disableAutoAnon = false;
  }
}

export async function signUpWithEmailPassword(email, password) {
  if (!authInstance) throw new Error('Auth not initialized');
  disableAutoAnon = true;
  try {
    if (authInstance.currentUser && authInstance.currentUser.isAnonymous) {
      // Link a new email/password credential to the existing anonymous user
      const cred = EmailAuthProvider.credential(email, password);
      try {
        const res = await linkWithCredential(authInstance.currentUser, cred);
        try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
        return res;
      } catch (linkErr) {
        try { await signOut(authInstance); } catch (_) {}
        if (linkErr?.code === 'auth/email-already-in-use' || linkErr?.code === 'auth/credential-already-in-use') {
          // An account with this email already exists — sign into it rather than
          // retrying createUserWithEmailAndPassword, which would just fail again.
          const res = await signInWithEmailAndPassword(authInstance, email, password);
          try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
          return res;
        }
        const res = await createUserWithEmailAndPassword(authInstance, email, password);
        try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
        return res;
      }
    }
    try {
      const res = await createUserWithEmailAndPassword(authInstance, email, password);
      try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
      return res;
    } catch (createErr) {
      if (createErr?.code === 'auth/email-already-in-use') {
        // Same fallback for a non-anonymous session hitting "Create account" on an existing email.
        const res = await signInWithEmailAndPassword(authInstance, email, password);
        try { localStorage.setItem('ff:hasAccount', '1'); } catch (_) {}
        return res;
      }
      throw createErr;
    }
  } finally {
    disableAutoAnon = false;
  }
}

export async function sendPasswordReset(email) {
  if (!authInstance) throw new Error('Auth not initialized');
  await sendPasswordResetEmail(authInstance, email);
}
